/* CUSTOM_WAKEBATCH_BY_CLAUDE (AIOPS 36026e0abf08, Ryan 2026-10-04) - AI Agents -> Activity: Wake batching.
   ─────────────────────────────────────────────────────────────────────────────
   The switch and the dials for the rfq-worker's per-seat wake queue (handlers/wake-batch-routes.js): non-urgent wakes
   wait until N are queued or the oldest has waited T minutes, then go to the seat as ONE wake. Weekdays and weekends
   have their own N / T. The urgent list is editable here (Ryan: tune it without a build). Below: what each seat has
   waiting right now (Release now), and the deals under a priority watch (End).

   Reads GET /admin/wake-batch; saves POST /admin/wake-batch-config; releases POST /admin/wake-batch-flush; ends a watch
   POST /priority-watch {ref, clear:true}. Admin only - a rep's token gets 403 and the panel draws nothing.
   ⛔ A FAILED READ SAYS SO. An empty queue list must never be confused with a panel that could not load.
   The section is at its size cap, so this file owns its styles and its fetch. `wakeBatchPanelHtml` is pure and
   exported for tests/wake-batch-panel-tests.mjs. */
(function (root) {
  'use strict';
  var API = 'https://rfq-worker.ryan-29b.workers.dev';
  var DATA = null;
  var ACK = null; // CUSTOM_WAKEACK_BY_CLAUDE - GET /admin/wake-ack-config

  function esc(v) {
    return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function when(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Denver', weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' MT';
    } catch (e) { return String(iso); }
  }

  var CSS = ''
    + '.rqwb__row{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:0 0 10px;}'
    + '.rqwb__lbl{font-size:12px;font-weight:600;color:var(--rq-text-muted,#8892a0);min-width:84px;}'
    + '.rqwb__num{width:58px;padding:4px 6px;font:inherit;font-size:13px;}'
    + '.rqwb__urgent{width:100%;min-height:54px;font:inherit;font-size:12px;padding:6px;box-sizing:border-box;}'
    + '.rqwb__state{font-size:13px;font-weight:700;}.rqwb__state--on{color:var(--rq-success,#16a34a);}.rqwb__state--off{color:var(--rq-text-muted,#8892a0);}'
    + '.rqwb__table{width:100%;border-collapse:collapse;font-size:13px;margin:6px 0 12px;}'
    + '.rqwb__table th{text-align:left;font-size:11px;font-weight:700;color:var(--rq-text-muted,#8892a0);text-transform:uppercase;padding:5px 6px;border-bottom:1px solid var(--rq-border,#1e2a42);}'
    + '.rqwb__table td{padding:6px;border-bottom:1px solid var(--rq-border,#1e2a42);vertical-align:top;}'
    + '.rqwb__msg{font-size:12px;margin:6px 0;}'
    + '.rqwb__sub{font-size:12px;color:var(--rq-text-muted,#8892a0);margin:4px 0 10px;}'
    + '@media (max-width:749px){.rqwb__table .rqwb__hide-m{display:none;}}';

  function injectCss() {
    if (typeof document === 'undefined' || document.getElementById('rqwb-css')) return;
    var st = document.createElement('style');
    st.id = 'rqwb-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  function numInput(id, value, min, max) {
    return '<input type="number" class="rqwb__num" id="' + id + '" min="' + min + '" max="' + max + '" value="' + esc(value) + '">';
  }

  /** The whole panel. Pure. */
  function wakeBatchPanelHtml(data, opts) {
    opts = opts || {};
    var h = '<div class="rqa-wake"><div class="rqa-wake__head"><span class="rqa-wake__title">Wake batching</span>'
      + '<button class="rqa-btn" type="button" data-wbrefresh="1">Refresh</button></div>';
    if (opts.error) return h + '<p class="rqwb__msg" style="color:#dc2626;">Could not load: ' + esc(opts.error) + '. Nothing below is current.</p></div>';
    if (!data) return h + '<p class="rqwb__sub">Loading&hellip;</p></div>';
    var c = data.config || {};
    var wd = c.weekday || {}, we = c.weekend || {};
    var win = data.windowNow || {};
    h += '<p class="rqa-coverage" style="margin:0 0 11px;">Every wake is a full agent run. When this is on, a wake that is not urgent waits for its seat until '
      + 'enough are queued or the oldest has waited long enough, then they all go as <b>one</b> wake. Each wake inside it keeps its own id, so nothing is merged or lost. '
      + 'Urgent wakes never wait, and one going to a seat takes that seat\'s queue with it.</p>';
    h += '<div class="rqwb__row"><span class="rqwb__lbl">Batching</span><span class="rqwb__state rqwb__state--' + (c.enabled ? 'on">ON' : 'off">OFF') + '</span>'
      + '<button class="rqa-btn" type="button" data-wbtoggle="' + (c.enabled ? 'off' : 'on') + '">' + (c.enabled ? 'Turn off' : 'Turn on') + '</button>'
      + (c.enabled ? '<span class="rqwb__sub" style="margin:0;">Right now (' + (win.weekend ? 'weekend' : 'weekday') + '): ' + esc(win.count) + ' wakes or ' + esc(win.minutes) + ' minutes.</span>' : '')
      + '</div>';
    h += '<div class="rqwb__row"><span class="rqwb__lbl">Weekdays</span>' + numInput('rqwb-wd-n', wd.count, 1, 25) + ' wakes or ' + numInput('rqwb-wd-t', wd.minutes, 5, 240) + ' minutes</div>';
    h += '<div class="rqwb__row"><span class="rqwb__lbl">Sat &amp; Sun</span>' + numInput('rqwb-we-n', we.count, 1, 25) + ' wakes or ' + numInput('rqwb-we-t', we.minutes, 5, 240) + ' minutes</div>';
    h += '<div class="rqwb__lbl" style="margin:0 0 4px;">Urgent wakes (never wait), comma separated</div>'
      + '<textarea class="rqwb__urgent" id="rqwb-urgent">' + esc((c.urgentEvents || []).join(', ')) + '</textarea>'
      + '<p class="rqwb__sub">Always urgent as well: a customer\'s or Ryan\'s email, and any wake about a deal under a priority watch.</p>'
      + '<div class="rqwb__row"><button class="rqa-btn" type="button" data-wbsave="1">Save</button><span class="rqwb__msg" id="rqwb-msg"></span></div>';

    var qs = data.queues || [];
    h += '<div class="rqwb__lbl" style="margin:8px 0 2px;">Waiting now</div>';
    if (!qs.length) h += '<p class="rqwb__sub">No wakes waiting for any seat.</p>';
    else {
      h += '<table class="rqwb__table"><thead><tr><th>Seat</th><th>Waiting</th><th class="rqwb__hide-m">Oldest</th><th>Goes by</th><th></th></tr></thead><tbody>';
      qs.forEach(function (q) {
        h += '<tr><td>' + esc(q.seat) + '</td><td>' + esc(q.count) + '</td><td class="rqwb__hide-m">' + esc(when(q.oldestAt)) + '</td><td>' + esc(when(q.releasesBy)) + '</td>'
          + '<td><button class="rqa-btn" type="button" data-wbflush="' + esc(q.seat) + '">Release now</button></td></tr>';
      });
      h += '</tbody></table>';
    }
    var ws = data.priorityWatches || [];
    h += '<div class="rqwb__lbl" style="margin:8px 0 2px;">Priority watches</div>'
      + '<p class="rqwb__sub">A deal with a live problem: every wake about it goes at once. Set by an agent holding the priority-watch permission, always with an end date (14 days at most).</p>';
    if (!ws.length) h += '<p class="rqwb__sub">No deal is under a priority watch.</p>';
    else {
      h += '<table class="rqwb__table"><thead><tr><th>Deal</th><th>Until</th><th class="rqwb__hide-m">Why</th><th class="rqwb__hide-m">By</th><th></th></tr></thead><tbody>';
      ws.forEach(function (w) {
        h += '<tr><td>' + esc(w.ref) + '</td><td>' + esc(when(w.until)) + '</td><td class="rqwb__hide-m">' + esc(w.reason) + '</td><td class="rqwb__hide-m">' + esc(w.by) + '</td>'
          + '<td><button class="rqa-btn" type="button" data-wbend="' + esc(w.ref) + '">End</button></td></tr>';
      });
      h += '</tbody></table>';
    }
    if (opts.ack) h += wakeAckSectionHtml(opts.ack);
    return h + '</div>';
  }

  /* CUSTOM_WAKEACK_BY_CLAUDE (ONYX 33b083c2848d, Ryan 2026-10-07) - urgent wake check-ins: an urgent wake carries `ack`;
     nobody acknowledges it in 15 minutes -> sent once more; still nobody 15 minutes later -> Ryan is emailed. Two switches,
     both OFF until clicked here (POST /admin/wake-ack-config). Pure. */
  function wakeAckSectionHtml(ack) {
    var c = (ack && ack.config) || {};
    var u = (ack && ack.unacked) || {};
    function sw(key, on, label) {
      return '<div class="rqwb__row"><span class="rqwb__lbl" style="min-width:210px;">' + label + '</span>'
        + '<span class="rqwb__state rqwb__state--' + (on ? 'on">ON' : 'off">OFF') + '</span>'
        + '<button class="rqa-btn" type="button" data-wback="' + key + '" data-wbackto="' + (on ? 'off' : 'on') + '">' + (on ? 'Turn off' : 'Turn on') + '</button></div>';
    }
    /* CUSTOM_WAKEEVERY_BY_CLAUDE (ONYX 71e3faa1efff, Ryan 2026-10-10) - EVERY wake now, three tries a gap apart, the email
       only for a work wake, the gap a setting here, and how long a "got it" really takes, so the email is switched on from
       numbers rather than a guess. */
    var g = Number(c.gapMinutes) || 15;
    var h = '<div class="rqwb__lbl" style="margin:14px 0 2px;">Wake check-ins</div>'
      + '<p class="rqwb__sub">Every wake asks the agent to acknowledge it ("got it", not "done"). Nobody does in ' + g + ' minutes: it is sent again, '
      + 'and once more ' + g + ' minutes later. Still nobody ' + g + ' minutes after the third try: for a wake that asks for work you get one email naming '
      + 'the agent, the wake and what it was supposed to do (three misses in a row usually means something is broken). An information-only wake is never '
      + 'emailed; its miss is just logged.</p>';
    h += sw('redeliver', !!c.redeliver, 'Send it again, up to twice');
    h += sw('emailRyan', !!c.emailRyan, 'Email me after the third miss');
    h += '<div class="rqwb__row"><span class="rqwb__lbl" style="min-width:210px;">Minutes between tries</span>'
      + numInput('rqwb-gap', g, 5, 60) + '<button class="rqa-btn" type="button" data-wbgap="1">Save</button></div>';
    var l = ack && ack.latency;
    if (l && l.acks) {
      var seats = Object.keys(l.bySeat || {}).sort().map(function (s) {
        var x = l.bySeat[s];
        return esc(s) + ' ' + x.acks + ' (typical ' + esc(x.medianMin) + ' min, slowest ' + esc(x.maxMin) + ')';
      }).join(', ');
      h += '<p class="rqwb__sub">How long a "got it" took, last 7 days: <b>' + l.acks + ' acknowledgements</b>, typical ' + esc(l.medianMin) + ' min, '
        + '9 in 10 within ' + esc(l.p90Min) + ' min, slowest ' + esc(l.maxMin) + ' min. '
        + '<b>' + l.afterAllTries + '</b> came after all three tries (' + (g * 3) + ' min), which is when you would have been emailed. ' + seats + '.</p>';
    } else {
      h += '<p class="rqwb__sub">How long a "got it" took: no acknowledgements in the last 7 days yet.</p>';
    }
    h += '<p class="rqwb__sub">Not acknowledged in the last 24 hours: <b>' + esc(u.text || '0') + '</b>'
      + (ack && ack.missed ? '. Missed after every try (still open, 3 days): <b>' + esc(ack.missed) + '</b>' : '')
      + (ack && ack.lastTick && ack.lastTick.at ? '. Last action ' + esc(when(ack.lastTick.at)) : '') + '.</p>';
    if (ack && ack.done) h += wakeDoneSectionHtml(ack); // CUSTOM_WAKEDONE_BY_CLAUDE
    return h;
  }

  /* CUSTOM_WAKEDONE_BY_CLAUDE (ONYX d43d1c54c930, Ryan 2026-10-12) - "got it" is not "done". A wake that asks for work also owes
     a done with proof (POST /wake-done). Got it but not done after N minutes: the agent is reminded, again N minutes later, and
     Ryan is emailed N minutes after that (the same "Email me" switch above). The list shows both times. Pure. */
  function wakeDoneSectionHtml(ack) {
    var c = (ack && ack.config) || {};
    var d = (ack && ack.done) || {};
    var m = Number(c.doneMinutes) || 30;
    var on = !!c.doneNudge;
    var h = '<div class="rqwb__lbl" style="margin:14px 0 2px;">Got it, then done</div>'
      + '<p class="rqwb__sub">A wake that asks for work also has to be reported done, with proof: a TEST number, a suggestion card, a skill edit, '
      + 'a deal note, or why nothing was needed. Got it but not done ' + m + ' minutes later: the agent is reminded to finish it, and again ' + m
      + ' minutes after that. Still not done ' + m + ' minutes after the second reminder: one email to you (the "Email me" switch above).</p>';
    h += '<div class="rqwb__row"><span class="rqwb__lbl" style="min-width:210px;">Remind the agent to finish it, twice</span>'
      + '<span class="rqwb__state rqwb__state--' + (on ? 'on">ON' : 'off">OFF') + '</span>'
      + '<button class="rqa-btn" type="button" data-wback="doneNudge" data-wbackto="' + (on ? 'off' : 'on') + '">' + (on ? 'Turn off' : 'Turn on') + '</button></div>';
    h += '<div class="rqwb__row"><span class="rqwb__lbl" style="min-width:210px;">Minutes before each reminder</span>'
      + numInput('rqwb-donegap', m, 10, 120) + '<button class="rqa-btn" type="button" data-wbdonegap="1">Save</button></div>';
    var waiting = d.waitingCount || 0;
    h += '<p class="rqwb__sub">Got it, not done yet: <b>' + esc(waiting) + '</b>'
      + ((d.waiting || []).length ? ' (' + d.waiting.map(function (w) {
        return esc(w.seats) + ' ' + esc(w.subject || w.event) + ' since ' + esc(when(w.receivedAt)) + (w.nudges ? ', reminded ' + esc(w.nudges) + 'x' : '');
      }).join('; ') + ')' : '') + '.</p>';
    var rows = d.recent || [];
    if (!rows.length) return h + '<p class="rqwb__sub">No wake reported done yet.</p>';
    h += '<table class="rqwb__table"><thead><tr><th>Wake</th><th>Agent</th><th class="rqwb__hide-m">Got it</th><th>Done</th><th class="rqwb__hide-m">Proof</th></tr></thead><tbody>';
    rows.forEach(function (r) {
      var doneCell = r.outcome === 'done' ? esc(when(r.doneAt))
        : (r.outcome === 'escalated' ? '<span style="color:#dc2626;">Not done, you were emailed</span>' : '<span style="color:#dc2626;">Not done</span>');
      h += '<tr><td>' + esc(r.subject || r.event) + '</td><td>' + esc(r.seat || '') + '</td><td class="rqwb__hide-m">' + esc(when(r.receivedAt)) + '</td>'
        + '<td>' + doneCell + '</td><td class="rqwb__hide-m">' + esc(r.evidence || '') + '</td></tr>';
    });
    return h + '</tbody></table>';
  }

  function getToken() {
    try {
      var s = sessionStorage.getItem('rep_portal_session') || localStorage.getItem('rep_portal_session');
      var j = s ? JSON.parse(s) : null;
      return j && j.accessToken ? j.accessToken : null;
    } catch (e) { return null; }
  }
  /* CUSTOM_TOKENREFRESH_BY_CLAUDE (Ryan 2026-10-10) - the access token lives 30 minutes. Ask the portal's own RepAuth for a
     fresh one (it renews inside the last 2 minutes) and, on a 401, renew once and try again, so a page left open does not
     answer "Could not load agents (401)" / "Unauthorized" until a hard refresh. */
  async function freshToken(force) {
    var A = (typeof window !== 'undefined' && window.RepPortal && window.RepPortal.Auth) || null;
    if (A) {
      try { return force ? await A.refreshTokens() : await A.getAccessToken(); } catch (e) { /* fall back to the stored token */ }
    }
    return getToken();
  }
  async function api(path, opts) {
    var send = function (t) { return fetch(API + path, Object.assign({}, opts, { headers: { Authorization: 'Bearer ' + t, 'Content-Type': 'application/json' } })); };
    var res = await send(await freshToken(false));
    if (res.status === 401) res = await send(await freshToken(true));
    var body = null;
    try { body = await res.json(); } catch (e) { body = null; }
    return { ok: res.ok, status: res.status, body: body };
  }
  function draw(error) {
    var host = document.getElementById('rqa-wakebatch');
    if (host) host.innerHTML = wakeBatchPanelHtml(DATA, { error: error, ack: ACK });
  }
  function say(text, bad) {
    var m = document.getElementById('rqwb-msg');
    if (m) m.innerHTML = '<span style="color:' + (bad ? '#dc2626' : 'var(--rq-success,#16a34a)') + ';">' + esc(text) + '</span>';
  }
  async function load() {
    if (!getToken()) return;
    var r = await api('/admin/wake-batch');
    if (r.status === 401 || r.status === 403) { var h = document.getElementById('rqa-wakebatch'); if (h) h.innerHTML = ''; return; }
    if (!r.ok) { DATA = null; draw((r.body && r.body.error) || ('HTTP ' + r.status)); return; }
    DATA = r.body;
    /* CUSTOM_WAKEACK_BY_CLAUDE - the check-in switches ride the same panel; a failed read just leaves the section out. */
    try { var a = await api('/admin/wake-ack-config'); ACK = a.ok ? a.body : null; } catch (e) { ACK = null; }
    draw();
  }
  /* A click while a save is running is ignored: after "Turn on" the panel redraws "Turn off" in the same spot, and a
     double click used to switch it straight back off (2026-10-04). */
  var BUSY = false;
  async function post(path, body, done) {
    if (BUSY) return;
    BUSY = true;
    var host = document.getElementById('rqa-wakebatch');
    if (host) host.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
    try {
      var r = await api(path, { method: 'POST', body: JSON.stringify(body) });
      if (!r.ok) { say('Not saved: ' + ((r.body && (r.body.error || r.body.why)) || r.status), true); return; }
      await load();
      say(done);
    } finally {
      BUSY = false;
      if (host) host.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
    }
  }
  function val(id) { var el = document.getElementById(id); return el ? el.value : ''; }

  function wire() {
    var host = document.getElementById('rqa-wakebatch');
    if (!host || host.__rqwb) return;
    host.__rqwb = true;
    host.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('button') : null;
      if (!b) return;
      if (b.getAttribute('data-wbrefresh')) { load(); return; }
      var t = b.getAttribute('data-wbtoggle');
      if (t === 'off' && !confirm('Turn wake batching off? Every wake will go out the moment it fires again.')) return;
      if (t) { post('/admin/wake-batch-config', { enabled: t === 'on' }, t === 'on' ? 'Batching is on.' : 'Batching is off. Anything waiting goes out on the next 5-minute tick.'); return; }
      if (b.getAttribute('data-wbsave')) {
        var urgent = val('rqwb-urgent').split(/[\s,]+/).map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
        post('/admin/wake-batch-config', {
          weekday: { count: Number(val('rqwb-wd-n')), minutes: Number(val('rqwb-wd-t')) },
          weekend: { count: Number(val('rqwb-we-n')), minutes: Number(val('rqwb-we-t')) },
          urgentEvents: urgent,
        }, 'Saved.');
        return;
      }
      if (b.getAttribute('data-wbdonegap')) {
        /* CUSTOM_WAKEDONE_BY_CLAUDE - minutes before each "finish this" reminder */
        var dm = Number(val('rqwb-donegap'));
        post('/admin/wake-ack-config', { doneMinutes: dm }, 'Reminders now go ' + dm + ' minutes apart.');
        return;
      }
      if (b.getAttribute('data-wbgap')) {
        /* CUSTOM_WAKEEVERY_BY_CLAUDE - the gap between tries */
        var gm = Number(val('rqwb-gap'));
        post('/admin/wake-ack-config', { gapMinutes: gm }, 'Wakes are now re-sent ' + gm + ' minutes apart.');
        return;
      }
      var k = b.getAttribute('data-wback');
      if (k) {
        var to = b.getAttribute('data-wbackto') === 'on';
        var body = {}; body[k] = to;
        post('/admin/wake-ack-config', body, (k === 'redeliver' ? 'Re-send' : (k === 'doneNudge' ? 'Finish-this reminders' : 'Email to you')) + ' is ' + (to ? 'on.' : 'off.'));
        return;
      }
      var f = b.getAttribute('data-wbflush');
      if (f) { post('/admin/wake-batch-flush', { seat: f }, 'Released ' + f + '\'s queue.'); return; }
      var w = b.getAttribute('data-wbend');
      if (w) { if (!confirm('End the priority watch on ' + w + '?')) return; post('/priority-watch', { ref: w, clear: true }, 'Watch on ' + w + ' ended.'); }
    });
  }

  function mount() { injectCss(); wire(); load(); }

  root.RqaWakeBatch = { wakeBatchPanelHtml: wakeBatchPanelHtml, wakeAckSectionHtml: wakeAckSectionHtml, wakeDoneSectionHtml: wakeDoneSectionHtml, mount: mount };
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
    else mount();
  }
})(typeof window !== 'undefined' ? window : globalThis);
/* END CUSTOM_WAKEBATCH_BY_CLAUDE */
